import { type FC, forwardRef, type Ref, useMemo } from 'react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { compareDesc } from 'date-fns';

import { Tooltip } from '@/components/ui/tooltip';
import { compareEnum } from '@/utils/compare-enum';

import { CreateAlertTask } from '../api/mutations/create-alert-task';
import { AlertStatus } from '../entities/alert-status';
import type { AlertSuggestion } from '../entities/alert-suggestion';
import { TaskStatus } from '../entities/task-status';

import { AlertSuggestionCard } from './alert-suggestion-card';

// -- TYPES --------------------------------------------------------------------

export type AlertSuggestionsColumnProps = {
  alertSuggestions: AlertSuggestion[];
  draggable?: boolean;
  onAlertTaskCreate: (input: CreateAlertTask.Input) => Promise<void>;
};

interface AlertSuggestionsListProps {
  alertSuggestions: AlertSuggestion[];
  draggable?: boolean;
  onAlertTaskCreate: (input: CreateAlertTask.Input) => Promise<void>;
}

// -- UTILS --------------------------------------------------------------------

const compareAlertStatus = compareEnum<AlertStatus>([
  AlertStatus.CRITICAL,
  AlertStatus.WARNING,
  AlertStatus.INFO,
]);

// -- SUBCOMPONENTS -----------------------------------------------------------

/**
 * Renders the header section of the AlertSuggestionsColumn.
 */
const AlertSuggestionsColumnHeader: FC = () => {
  return (
    <div className="w-full shrink-0 space-y-2">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="text-base font-normal leading-normal text-neutral-700">
          Suggested Tasks
        </div>
        <Tooltip
          clickToKeepOpen
          position="bottom"
          className="w-64"
          content={
            <div className="space-y-2">
              <p>
                Suggestions are based on recent alerts that aren't already
                tracked by a card.
              </p>
              <p>
                Move a suggestion to another column to create a card and track
                its progress.
              </p>
            </div>
          }
        >
          <QuestionMarkCircleIcon className="size-5 cursor-help text-neutral-900" />
        </Tooltip>
      </div>
      {/* Separation line */}
      <div className="h-1 w-full bg-stone-300" />
    </div>
  );
};

/**
 * Renders the list of AlertSuggestions, sorting by status and changed date,
 * then mapping to AlertSuggestionCard.
 */
const AlertSuggestionsList: FC<AlertSuggestionsListProps> = ({
  alertSuggestions,
  draggable,
  onAlertTaskCreate,
}) => {
  // We memoize the sorted list to avoid unnecessary re-sorting on re-renders
  const sortedAlertSuggestions = useMemo<AlertSuggestion[]>(
    () =>
      [...alertSuggestions] // avoid mutating the original array
        .sort((a, b) => compareDesc(a.changedAt, b.changedAt))
        .sort((a, b) => compareAlertStatus(a.status, b.status)),
    [alertSuggestions],
  );

  return (
    <div className="size-full grow overflow-y-auto py-4">
      <div className="divide-y divide-slate-200 border border-slate-200">
        {sortedAlertSuggestions.map((alertSuggestion) => (
          <AlertSuggestionCard
            key={alertSuggestion.id}
            draggable={draggable}
            alertSuggestion={alertSuggestion}
            onClick={() =>
              onAlertTaskCreate({
                status: TaskStatus.OPEN,
                alertSuggestion,
              })
            }
          />
        ))}
      </div>
    </div>
  );
};

// -- MAIN COMPONENT -----------------------------------------------------------

/**
 * Main column component that displays a list of AlertSuggestion items
 * with a header and additional tooltip information.
 */
export const AlertSuggestionsColumn = forwardRef<
  HTMLDivElement,
  AlertSuggestionsColumnProps
>(function AlertSuggestionsColumn(
  { alertSuggestions, draggable, onAlertTaskCreate },
  ref: Ref<HTMLDivElement>,
) {
  return (
    <div ref={ref} className="flex size-full flex-col overflow-hidden">
      <AlertSuggestionsColumnHeader />
      <AlertSuggestionsList
        alertSuggestions={alertSuggestions}
        draggable={draggable}
        onAlertTaskCreate={onAlertTaskCreate}
      />
    </div>
  );
});

AlertSuggestionsColumn.displayName = 'AlertSuggestionsColumn';
